import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconAdjustmentsHorizontal } from '@tabler/icons-react';
import { TeeButton } from '@/components/ui/tee-button';
import { useGamePlanOverview } from '@/hooks/useGamePlanTracker';
import { EditPlanModal } from './EditPlanModal';

interface AdjustPlanButtonProps {
  locationId: string;
  /**
   * Which surface it sits on. The Game Plan heroes are forest green, so the
   * button has to invert there; everything else sits on cream.
   */
  tone?: 'forest' | 'cream';
}

/**
 * The single, always-available way back into the goal and the Grand Opening
 * date.
 *
 * Both inputs were previously set once in onboarding and then locked away,
 * which made a slipped opening date or a revised target something owners had to
 * live with. This drops the same control onto every Game Plan surface — the
 * Roadmap arc, the Goal Board and This Week — so wherever the owner notices the
 * plan no longer matches reality, the fix is right there.
 *
 * It reads the live values from the overview query the host pages already run,
 * so adding it costs no extra request.
 */
export function AdjustPlanButton({ locationId, tone = 'cream' }: AdjustPlanButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data: overview } = useGamePlanOverview(locationId);
  const current = overview?.currentWeek;
  if (!current) return null;

  // On the forest heroes the standard secondary skin disappears into the dark
  // panel, so the button borrows the heroes' translucent-cream treatment.
  const forestCls =
    'border-[rgba(251,246,236,0.28)] bg-[rgba(251,246,236,0.12)] text-[var(--text-on-forest)] hover:bg-[rgba(251,246,236,0.2)] hover:border-[rgba(251,246,236,0.4)]';

  return (
    <>
      <TeeButton
        variant="secondary"
        size="s"
        leftIcon={<IconAdjustmentsHorizontal size={14} />}
        text={t('gamePlan.adjustPlan', 'Adjust goal & date')}
        onClick={() => setOpen(true)}
        className={tone === 'forest' ? forestCls : undefined}
      />
      <EditPlanModal
        isOpen={open}
        locationId={locationId}
        goal={current.goal}
        grandOpeningDate={current.grandOpeningDate}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
